import { Fragment, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, getDocs, orderBy, query, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';

const FALLBACK_SPORTS = [
  'Football', 'Rugby Union', 'Rugby League', 'Cricket', 'Hockey',
  'Basketball', 'Netball', 'Tennis', 'Golf', 'Swimming',
  'Athletics', 'Multi-Sport', 'Other',
];

const FALLBACK_GROUP_TYPES = [
  'School', 'College / 6th Form', 'University', 'Sports Club',
  'Corporate', 'Private Group', 'MIC Cup', 'Other',
];

const FALLBACK_CATEGORIES = [
  'Hotel & Food', 'Flights', 'Transport', 'Match Tickets', 'Stadium Tour',
  'Fixtures', 'Training Sessions', 'Padel', 'Yoga', 'Waterpark',
];

const QUANTITY_ADDONS = {
  'Fixtures':          { max: 5, label: 'Fixtures per squad' },
  'Training Sessions': { max: 5, label: 'Training sessions per squad' },
  'Padel':             { max: 3 },
  'Yoga':              { max: 3 },
};
const OPTION_ADDONS = {};

const STANDARD_PKG_NAMES = new Set(['Hotel & Food', 'Flights', 'Fixtures', 'Match Tickets', 'Training Sessions', 'Stadium Tour', 'Padel', 'Transport']);

const SPORT_TILES = [
  { name: 'Football',   emoji: '⚽' },
  { name: 'Rugby',      emoji: '🏉' },
  { name: 'Basketball', emoji: '🏀' },
  { name: 'Tennis',     emoji: '🎾' },
  { name: 'Swimming',   emoji: '🏊' },
  { name: 'Hockey',     emoji: '🏑' },
  { name: 'Cricket',    emoji: '🏏' },
];

// Step 1 = Contact, Step 2 = Tour Details, Step 3 = Group Info, Step 4 = Review
const STEP_LABELS = ['Contact', 'Tour Details', 'Group Info', 'Review'];

const BLANK_FORM = {
  contactName: '', phone: '',
  sport: '', destination: '', departureDate: '', returnDate: '',
  groupType: '', participants: '', participantsMale: '', participantsFemale: '',
  supportersMale: '', supportersFemale: '',
  customerStaffMale: '', customerStaffFemale: '',
  notes: '',
};

const DRAFT_KEY = 'mst_new_request_draft';

function saveDraft(form, selectedAddons, packageMode, step, keyContacts) {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ form, selectedAddons, packageMode, step, keyContacts }));
  } catch {}
}

function loadDraft() {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function clearDraft() {
  try { sessionStorage.removeItem(DRAFT_KEY); } catch {}
}

function CheckIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function FieldError({ msg }) {
  if (!msg) return null;
  return <p className="mt-1 text-[12px] text-dr">{msg}</p>;
}

function formatDate(d) {
  if (!d) return '—';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

function ReviewRow({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex gap-3">
      <span className="text-[13px] text-[#6B7280] w-40 flex-shrink-0">{label}</span>
      <span className="text-[13px] text-[#111827] font-medium">{value}</span>
    </div>
  );
}

export default function NewRequest() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const draft = loadDraft();
  const [step, setStep] = useState(draft?.step ?? 1);
  const [fieldErrors, setFieldErrors] = useState({});
  const [form, setForm] = useState(draft?.form ?? BLANK_FORM);
  const [showMoreSports, setShowMoreSports] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasDraft] = useState(!!draft);

  const [sportsList, setSportsList] = useState(FALLBACK_SPORTS);
  const [categories, setCategories] = useState([]);
  const [groupTypesList, setGroupTypesList] = useState(FALLBACK_GROUP_TYPES);
  const [selectedAddons, setSelectedAddons] = useState(draft?.selectedAddons ?? {});
  const [packageMode, setPackageMode] = useState(draft?.packageMode ?? 'standard');
  const [keyContacts, setKeyContacts] = useState(draft?.keyContacts ?? []);

  useEffect(() => {
    getDocs(query(collection(db, 'sports'), orderBy('order', 'asc'))).then((snap) => {
      if (!snap.empty) {
        setSportsList(
          snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((s) => s.active !== false).map((s) => s.name)
        );
      }
    }).catch(() => {});

    getDocs(query(collection(db, 'tourCategories'), orderBy('order', 'asc'))).then((snap) => {
      if (!snap.empty) setCategories(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      else setCategories(FALLBACK_CATEGORIES.map((name, i) => ({ id: name, name, order: i })));
    }).catch(() => {
      setCategories(FALLBACK_CATEGORIES.map((name, i) => ({ id: name, name, order: i })));
    });

    getDocs(query(collection(db, 'groupTypes'), orderBy('order', 'asc'))).then((snap) => {
      if (!snap.empty) {
        setGroupTypesList(
          snap.docs.map((d) => d.data()).filter((g) => g.active !== false).map((g) => g.name)
        );
      }
    }).catch(() => {});
  }, []);

  // Apply standard package when mode is standard and categories/dates change
  useEffect(() => {
    if (categories.length === 0 || packageMode !== 'standard') return;
    const nights = (() => {
      if (!form.departureDate || !form.returnDate) return 2;
      const diff = Math.round((new Date(form.returnDate) - new Date(form.departureDate)) / 86400000);
      return diff > 0 ? diff : 2;
    })();
    const fixtureQty = nights >= 3 ? 2 : 1;
    const defaults = {};
    categories.forEach((cat) => {
      if (!STANDARD_PKG_NAMES.has(cat.name)) return;
      const base = { name: cat.name, notes: '' };
      if (cat.name === 'Fixtures') defaults[cat.id] = { ...base, quantity: fixtureQty };
      else if (cat.name === 'Training Sessions') defaults[cat.id] = { ...base, quantity: 2 };
      else if (QUANTITY_ADDONS[cat.name]) defaults[cat.id] = { ...base, quantity: 1 };
      else if (cat.name === 'Flights') defaults[cat.id] = { ...base, largeCaseQuantity: 0 };
      else defaults[cat.id] = base;
    });
    setSelectedAddons(defaults);
  }, [packageMode, form.departureDate, form.returnDate, categories]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    saveDraft(form, selectedAddons, packageMode, step, keyContacts);
  }, [form, selectedAddons, packageMode, step]);

  function clearError(key) {
    setFieldErrors((p) => { const n = { ...p }; delete n[key]; return n; });
  }

  function handleChange(e) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    clearError(e.target.name);
  }

  function handleBlur(e) {
    const { name, value } = e.target;
    let err = null;
    if (name === 'contactName' && !value.trim()) err = 'Contact name is required.';
    if (name === 'destination' && !value.trim()) err = 'Destination is required.';
    if (name === 'departureDate' && !value) err = 'Departure date is required.';
    if (name === 'returnDate') {
      if (!value) err = 'Return date is required.';
      else if (form.departureDate && value < form.departureDate) err = 'Return date must be after departure date.';
    }
    if (err) setFieldErrors((p) => ({ ...p, [name]: err }));
  }

  function setSport(s) {
    setForm((prev) => ({ ...prev, sport: s }));
    setShowMoreSports(false);
    clearError('sport');
  }

  function adjustCount(name, delta) {
    setForm((prev) => ({
      ...prev,
      [name]: String(Math.max(0, (parseInt(prev[name]) || 0) + delta)),
    }));
  }

  function toggleAddon(cat) {
    setSelectedAddons((prev) => {
      if (prev[cat.id]) {
        const next = { ...prev };
        delete next[cat.id];
        return next;
      }
      const base = { name: cat.name, notes: '' };
      if (QUANTITY_ADDONS[cat.name]) return { ...prev, [cat.id]: { ...base, quantity: 1 } };
      if (cat.name === 'Flights') return { ...prev, [cat.id]: { ...base, largeCaseQuantity: 0 } };
      return { ...prev, [cat.id]: base };
    });
  }

  function updateAddonQuantity(catId, qty) {
    setSelectedAddons((prev) => ({ ...prev, [catId]: { ...prev[catId], quantity: qty } }));
  }

  function updateAddonNotes(catId, notes) {
    setSelectedAddons((prev) => ({ ...prev, [catId]: { ...prev[catId], notes } }));
  }

  function updateAddonLargeCaseQty(catId, qty) {
    setSelectedAddons((prev) => ({ ...prev, [catId]: { ...prev[catId], largeCaseQuantity: Math.max(0, qty) } }));
  }

  function validateStep(n) {
    const errs = {};
    if (n === 1) {
      if (!form.contactName.trim()) errs.contactName = 'Contact name is required.';
    }
    if (n === 2) {
      if (!form.sport) errs.sport = 'Please select a sport.';
      if (!form.destination.trim()) errs.destination = 'Destination is required.';
      if (!form.departureDate) errs.departureDate = 'Departure date is required.';
      if (!form.returnDate) errs.returnDate = 'Return date is required.';
      if (form.returnDate && form.departureDate && form.returnDate < form.departureDate) {
        errs.returnDate = 'Return date must be after departure date.';
      }
    }
    if (n === 3) {
      if (!form.groupType) errs.groupType = 'Group type is required.';
      if (!form.participantsMale && !form.participantsFemale) errs.participantsMale = 'Please enter the number of male and/or female players.';
    }
    return errs;
  }

  function goNext() {
    const errs = validateStep(step);
    if (Object.keys(errs).length > 0) { setFieldErrors(errs); return; }
    setFieldErrors({});
    setStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function goBack() {
    setFieldErrors({});
    setStep((s) => s - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function handleSubmit() {
    const addonsArray = Object.entries(selectedAddons).map(([id, val]) => ({
      categoryId: id,
      categoryName: val.name,
      notes: val.notes || '',
      ...(val.quantity !== undefined ? { quantity: val.quantity } : {}),
    }));

    const flightsAddon = Object.values(selectedAddons).find((v) => v.name === 'Flights');
    const largeCaseQuantity = flightsAddon ? Number(flightsAddon.largeCaseQuantity) || 0 : 0;

    setSubmitting(true);
    setFieldErrors({});
    try {
      await addDoc(collection(db, 'requests'), {
        customerId: currentUser.uid,
        customerEmail: currentUser.email,
        leaderCostBasis: 'days', // Tour/Junior Lead costed by calendar days present, not nights stayed
        sport: form.sport,
        destination: form.destination,
        departureDate: form.departureDate,
        returnDate: form.returnDate,
        groupType: form.groupType,
        participants: (Number(form.participantsMale) || 0) + (Number(form.participantsFemale) || 0),
        contactName: form.contactName,
        phone: form.phone,
        notes: form.notes,
        keyContacts: keyContacts.filter((c) => c.name.trim()),
        selectedAddons: addonsArray,
        largeCaseQuantity,
        participantsMale: form.participantsMale ? Number(form.participantsMale) : null,
        participantsFemale: form.participantsFemale ? Number(form.participantsFemale) : null,
        supportersMale: form.supportersMale ? Number(form.supportersMale) : 0,
        supportersFemale: form.supportersFemale ? Number(form.supportersFemale) : 0,
        customerStaffMale: form.customerStaffMale ? Number(form.customerStaffMale) : 0,
        customerStaffFemale: form.customerStaffFemale ? Number(form.customerStaffFemale) : 0,
        customerStaff: (Number(form.customerStaffMale) || 0) + (Number(form.customerStaffFemale) || 0),
        status: 'submitted',
        category: 'sales_admin',
        archived: false,
        stageProgress: {},
        createdAt: serverTimestamp(),
      });
      clearDraft();
      navigate('/customer', { state: { submitted: true } });
    } catch (err) {
      console.error(err);
      setFieldErrors({ submit: 'Something went wrong. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  }

  const isTileSport = SPORT_TILES.some((t) => t.name === form.sport);

  return (
    <div className="min-h-screen bg-page">
      {/* ─── Page header ──────────────────────────────────────────────────────── */}
      <nav className="bg-white border-b border-[#E5E7EB] px-6 py-4 flex items-center gap-4">
        <button onClick={() => navigate('/customer')} className="text-[13px] text-[#9CA3AF] hover:text-[#374151] font-medium">
          ← Back
        </button>
        <h1 className="text-[20px] font-bold text-[#111827]">New Tour Request</h1>
      </nav>

      <div className="max-w-2xl mx-auto px-6 py-10 space-y-6">
        {hasDraft && (
          <div className="flex items-center justify-between bg-amber-light border border-ba/30 rounded-[8px] px-4 py-3 text-[13px] text-[#92400E]">
            <span>Your previous draft has been restored.</span>
            <button
              onClick={() => {
                clearDraft();
                setForm(BLANK_FORM);
                setSelectedAddons({});
                setPackageMode('standard');
                setStep(1);
              }}
              className="font-medium underline hover:no-underline ml-4"
            >
              Start fresh
            </button>
          </div>
        )}
        <p className="text-[14px] text-[#6B7280]">Tell us about your trip and we'll come back to you with a tailored quote.</p>

        {/* ─── Step indicator ───────────────────────────────────────────────── */}
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] px-6 py-5">
          <div className="flex items-start">
            {STEP_LABELS.map((label, i) => {
              const n = i + 1;
              const done = n < step;
              const current = n === step;
              return (
                <Fragment key={n}>
                  {i > 0 && (
                    <div className={`flex-1 h-px mt-4 mx-2 ${done ? 'bg-pb' : 'bg-[#E5E7EB]'}`} />
                  )}
                  <div className="flex flex-col items-center gap-1.5">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-bold border-2 transition-all ${
                      done    ? 'bg-pb border-pb text-white' :
                      current ? 'border-pb text-pb bg-blue-light' :
                                'border-[#E5E7EB] text-[#9CA3AF] bg-white'
                    }`}>
                      {done ? <CheckIcon /> : n}
                    </div>
                    <span className={`text-[11px] font-medium whitespace-nowrap ${
                      current ? 'text-pb' : done ? 'text-[#374151]' : 'text-[#9CA3AF]'
                    }`}>{label}</span>
                  </div>
                </Fragment>
              );
            })}
          </div>
        </div>

        {/* ─── Step 1: Contact ──────────────────────────────────────────────── */}
        {step === 1 && (
          <section className="bg-white rounded-[12px] border border-[#E5E7EB] p-6 space-y-5">
            <h2 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Your Contact Details</h2>
            <p className="text-[14px] text-[#6B7280]">Who should we contact about this request?</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1">Contact Name <span className="text-dr">*</span></label>
                <input
                  type="text" name="contactName" value={form.contactName} onChange={handleChange} onBlur={handleBlur}
                  placeholder="Full name"
                  className={`w-full border rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb ${fieldErrors.contactName ? 'border-dr' : 'border-[#E5E7EB]'}`}
                />
                <FieldError msg={fieldErrors.contactName} />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1">Phone</label>
                <input
                  type="tel" name="phone" value={form.phone} onChange={handleChange}
                  placeholder="Optional"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
              </div>
            </div>

            {/* Additional key contacts */}
            {keyContacts.length > 0 && (
              <div className="space-y-3 pt-2 border-t border-[#F1F3F6]">
                <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Additional Key Contacts</p>
                {keyContacts.map((c, i) => (
                  <div key={i} className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-start p-3 bg-[#F9FAFB] rounded-[8px]">
                    <input
                      value={c.name} placeholder="Full name"
                      onChange={(e) => setKeyContacts((p) => p.map((k, idx) => idx === i ? { ...k, name: e.target.value } : k))}
                      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white"
                    />
                    <input
                      value={c.phone} placeholder="Phone (optional)"
                      onChange={(e) => setKeyContacts((p) => p.map((k, idx) => idx === i ? { ...k, phone: e.target.value } : k))}
                      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white"
                    />
                    <div className="flex gap-2 items-center">
                      <input
                        value={c.email} placeholder="Email (optional)"
                        onChange={(e) => setKeyContacts((p) => p.map((k, idx) => idx === i ? { ...k, email: e.target.value } : k))}
                        className="flex-1 border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white"
                      />
                      <button
                        type="button"
                        onClick={() => setKeyContacts((p) => p.filter((_, idx) => idx !== i))}
                        className="w-7 h-7 flex items-center justify-center text-[#9CA3AF] hover:text-dr text-lg flex-shrink-0"
                        title="Remove contact"
                      >×</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={() => setKeyContacts((p) => [...p, { name: '', phone: '', email: '' }])}
              className="flex items-center gap-1.5 text-[13px] font-semibold text-pb hover:text-navy transition"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              Add a key contact
            </button>
          </section>
        )}

        {/* ─── Step 2: Tour Details ─────────────────────────────────────────── */}
        {step === 2 && (
          <div className="space-y-5">
            <section className="bg-white rounded-[12px] border border-[#E5E7EB] p-6 space-y-5">
              <h2 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Tour Details</h2>

              {/* Sport tiles */}
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-2">
                  Sport <span className="text-dr">*</span>
                </label>
                <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                  {SPORT_TILES.map((tile) => (
                    <button
                      key={tile.name}
                      type="button"
                      onClick={() => setSport(tile.name)}
                      className={`flex flex-col items-center gap-1 py-3 px-2 rounded-[10px] border-2 text-[11px] font-medium transition ${
                        form.sport === tile.name
                          ? 'border-pb bg-blue-light text-pb'
                          : 'border-[#E5E7EB] bg-white text-[#6B7280] hover:border-[#D1D5DB] hover:bg-surface'
                      }`}
                    >
                      <span className="text-2xl leading-none">{tile.emoji}</span>
                      <span>{tile.name}</span>
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setShowMoreSports((v) => !v)}
                    className={`flex flex-col items-center gap-1 py-3 px-2 rounded-[10px] border-2 text-[11px] font-medium transition ${
                      !isTileSport && form.sport
                        ? 'border-pb bg-blue-light text-pb'
                        : 'border-[#E5E7EB] bg-white text-[#6B7280] hover:border-[#D1D5DB] hover:bg-surface'
                    }`}
                  >
                    <span className="text-2xl leading-none">⋯</span>
                    <span>{!isTileSport && form.sport ? form.sport : 'More'}</span>
                  </button>
                </div>
                {showMoreSports && (
                  <div className="mt-3">
                    <select
                      value={form.sport}
                      onChange={(e) => setSport(e.target.value)}
                      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                      autoFocus
                    >
                      <option value="">Select a sport…</option>
                      {sportsList.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                )}
                <FieldError msg={fieldErrors.sport} />
              </div>

              {/* Destination + dates */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <label className="block text-[13px] font-medium text-[#374151] mb-1">Destination <span className="text-dr">*</span></label>
                  <input
                    type="text" name="destination" value={form.destination} onChange={handleChange} onBlur={handleBlur}
                    placeholder="e.g. Barcelona, Spain"
                    className={`w-full border rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb ${fieldErrors.destination ? 'border-dr' : 'border-[#E5E7EB]'}`}
                  />
                  <FieldError msg={fieldErrors.destination} />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#374151] mb-1">Departure Date <span className="text-dr">*</span></label>
                  <input
                    type="date" name="departureDate" value={form.departureDate} onChange={handleChange} onBlur={handleBlur}
                    className={`w-full border rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb ${fieldErrors.departureDate ? 'border-dr' : 'border-[#E5E7EB]'}`}
                  />
                  <FieldError msg={fieldErrors.departureDate} />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#374151] mb-1">Return Date <span className="text-dr">*</span></label>
                  <input
                    type="date" name="returnDate" value={form.returnDate} onChange={handleChange} onBlur={handleBlur}
                    className={`w-full border rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb ${fieldErrors.returnDate ? 'border-dr' : 'border-[#E5E7EB]'}`}
                  />
                  <FieldError msg={fieldErrors.returnDate} />
                </div>
              </div>
            </section>

            {/* What's included */}
            <section className="bg-white rounded-[12px] border border-[#E5E7EB] p-6 space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <h2 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">What's Included</h2>
                  <div className="flex rounded-[8px] border border-[#E5E7EB] overflow-hidden text-[12px]">
                    <button type="button" onClick={() => setPackageMode('standard')}
                      className={`px-3 py-1.5 font-medium transition ${packageMode === 'standard' ? 'bg-pb text-white' : 'bg-white text-[#6B7280] hover:bg-[#F3F4F6]'}`}>
                      Standard package
                    </button>
                    <button type="button" onClick={() => setPackageMode('bespoke')}
                      className={`px-3 py-1.5 font-medium border-l border-[#E5E7EB] transition ${packageMode === 'bespoke' ? 'bg-pb text-white' : 'bg-white text-[#6B7280] hover:bg-[#F3F4F6]'}`}>
                      Bespoke
                    </button>
                  </div>
                </div>
                <p className="text-[12px] text-[#9CA3AF]">
                  {packageMode === 'standard'
                    ? 'Pre-filled based on your trip duration — you can still edit below.'
                    : 'Select everything you\'d like included. Hotel & Food and Flights are pre-selected.'}
                </p>
              </div>
              {categories.length === 0 ? (
                <p className="text-[13px] text-[#9CA3AF]">Loading options…</p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2">
                    {categories.filter((c) => c.active !== false).map((cat) => {
                      const selected = !!selectedAddons[cat.id];
                      return (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => toggleAddon(cat)}
                          className={`px-3.5 py-1.5 rounded-[20px] border text-[13px] font-medium transition ${
                            selected
                              ? 'bg-pb border-pb text-white'
                              : 'bg-white border-[#E5E7EB] text-[#374151] hover:border-pb/40 hover:bg-blue-light'
                          }`}
                        >
                          {cat.name}
                        </button>
                      );
                    })}
                  </div>

                  {Object.keys(selectedAddons).length > 0 && (
                    <div className="space-y-4 pt-3 border-t border-[#F3F4F6]">
                      <p className="text-[11px] font-medium text-[#9CA3AF] uppercase tracking-[0.9px]">Additional preferences</p>
                      {Object.entries(selectedAddons).map(([id, val]) => {
                        const qtyConfig = QUANTITY_ADDONS[val.name];

                        if (qtyConfig) return (
                          <div key={id}>
                            <label className="block text-[13px] font-medium text-[#374151] mb-2">{qtyConfig.label || val.name}</label>
                            <div className="flex gap-2">
                              {Array.from({ length: qtyConfig.max }, (_, i) => i + 1).map((n) => (
                                <button key={n} type="button" onClick={() => updateAddonQuantity(id, n)}
                                  className={`w-10 h-10 rounded-[8px] text-[13px] font-semibold border transition ${val.quantity === n ? 'bg-pb border-pb text-white' : 'bg-white border-[#E5E7EB] text-[#374151] hover:border-pb/40'}`}>
                                  {n}
                                </button>
                              ))}
                            </div>
                          </div>
                        );

                        return (
                          <div key={id} className="space-y-2">
                            <label className="block text-[13px] font-medium text-[#374151]">{val.name}</label>
                            {val.name === 'Flights' && (
                              <div className="flex items-center gap-3">
                                <span className="text-[13px] text-[#6B7280]">Large kit cases (group total)</span>
                                <button type="button" onClick={() => updateAddonLargeCaseQty(id, (val.largeCaseQuantity || 0) - 1)}
                                  className="w-7 h-7 rounded-full border border-[#E5E7EB] text-[#374151] hover:border-pb/40 flex items-center justify-center text-[16px] leading-none">−</button>
                                <span className="w-6 text-center text-[14px] font-semibold text-[#111827]">{val.largeCaseQuantity || 0}</span>
                                <button type="button" onClick={() => updateAddonLargeCaseQty(id, (val.largeCaseQuantity || 0) + 1)}
                                  className="w-7 h-7 rounded-full border border-[#E5E7EB] text-[#374151] hover:border-pb/40 flex items-center justify-center text-[16px] leading-none">+</button>
                                <span className="text-[11px] text-[#9CA3AF]">0 = hand luggage only</span>
                              </div>
                            )}
                            <input type="text" value={val.notes} onChange={(e) => updateAddonNotes(id, e.target.value)}
                              placeholder={`Any specific requirements for ${val.name.toLowerCase()}…`}
                              className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb" />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </section>
          </div>
        )}

        {/* ─── Step 3: Group Info ───────────────────────────────────────────── */}
        {step === 3 && (
          <section className="bg-white rounded-[12px] border border-[#E5E7EB] p-6 space-y-5">
            <h2 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Group Info</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1">Group Type <span className="text-dr">*</span></label>
                <select
                  name="groupType" value={form.groupType} onChange={handleChange}
                  className={`w-full border rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb ${fieldErrors.groupType ? 'border-dr' : 'border-[#E5E7EB]'}`}
                >
                  <option value="">Select group type</option>
                  {groupTypesList.map((g) => <option key={g}>{g}</option>)}
                </select>
                <FieldError msg={fieldErrors.groupType} />
              </div>

              {/* Players male/female grouped */}
              <div className="sm:col-span-2">
                <label className="block text-[13px] font-medium text-[#374151] mb-2">Approx Players/Students <span className="text-dr">*</span></label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[12px] text-[#6B7280] mb-1">Male</label>
                    <div className="flex items-center">
                      <button type="button" onClick={() => adjustCount('participantsMale', -1)}
                        className="w-9 h-9 rounded-l-[8px] border border-r-0 border-[#E5E7EB] bg-surface text-[#6B7280] hover:bg-[#F3F4F6] text-lg font-medium transition flex items-center justify-center">−</button>
                      <input type="number" name="participantsMale" value={form.participantsMale} onChange={handleChange}
                        min="0" placeholder="0"
                        className={`w-full h-9 border text-center text-[14px] focus:outline-none focus:ring-2 focus:ring-pb ${fieldErrors.participantsMale ? 'border-dr' : 'border-[#E5E7EB]'}`} />
                      <button type="button" onClick={() => adjustCount('participantsMale', 1)}
                        className="w-9 h-9 rounded-r-[8px] border border-l-0 border-[#E5E7EB] bg-surface text-[#6B7280] hover:bg-[#F3F4F6] text-lg font-medium transition flex items-center justify-center">+</button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[12px] text-[#6B7280] mb-1">Female</label>
                    <div className="flex items-center">
                      <button type="button" onClick={() => adjustCount('participantsFemale', -1)}
                        className="w-9 h-9 rounded-l-[8px] border border-r-0 border-[#E5E7EB] bg-surface text-[#6B7280] hover:bg-[#F3F4F6] text-lg font-medium transition flex items-center justify-center">−</button>
                      <input type="number" name="participantsFemale" value={form.participantsFemale} onChange={handleChange}
                        min="0" placeholder="0"
                        className="w-full h-9 border border-[#E5E7EB] text-center text-[14px] focus:outline-none focus:ring-2 focus:ring-pb" />
                      <button type="button" onClick={() => adjustCount('participantsFemale', 1)}
                        className="w-9 h-9 rounded-r-[8px] border border-l-0 border-[#E5E7EB] bg-surface text-[#6B7280] hover:bg-[#F3F4F6] text-lg font-medium transition flex items-center justify-center">+</button>
                    </div>
                  </div>
                </div>
                <FieldError msg={fieldErrors.participantsMale} />
              </div>

              {/* Supporters */}
              <div className="sm:col-span-2">
                <label className="block text-[13px] font-medium text-[#374151] mb-2">Approx supporters/other travellers</label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[12px] text-[#6B7280] mb-1">Male</label>
                    <div className="flex items-center">
                      <button type="button" onClick={() => adjustCount('supportersMale', -1)}
                        className="w-9 h-9 rounded-l-[8px] border border-r-0 border-[#E5E7EB] bg-surface text-[#6B7280] hover:bg-[#F3F4F6] text-lg font-medium transition flex items-center justify-center">−</button>
                      <input type="number" name="supportersMale" value={form.supportersMale} onChange={handleChange}
                        min="0" placeholder="0"
                        className="w-full h-9 border border-[#E5E7EB] text-center text-[14px] focus:outline-none focus:ring-2 focus:ring-pb" />
                      <button type="button" onClick={() => adjustCount('supportersMale', 1)}
                        className="w-9 h-9 rounded-r-[8px] border border-l-0 border-[#E5E7EB] bg-surface text-[#6B7280] hover:bg-[#F3F4F6] text-lg font-medium transition flex items-center justify-center">+</button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[12px] text-[#6B7280] mb-1">Female</label>
                    <div className="flex items-center">
                      <button type="button" onClick={() => adjustCount('supportersFemale', -1)}
                        className="w-9 h-9 rounded-l-[8px] border border-r-0 border-[#E5E7EB] bg-surface text-[#6B7280] hover:bg-[#F3F4F6] text-lg font-medium transition flex items-center justify-center">−</button>
                      <input type="number" name="supportersFemale" value={form.supportersFemale} onChange={handleChange}
                        min="0" placeholder="0"
                        className="w-full h-9 border border-[#E5E7EB] text-center text-[14px] focus:outline-none focus:ring-2 focus:ring-pb" />
                      <button type="button" onClick={() => adjustCount('supportersFemale', 1)}
                        className="w-9 h-9 rounded-r-[8px] border border-l-0 border-[#E5E7EB] bg-surface text-[#6B7280] hover:bg-[#F3F4F6] text-lg font-medium transition flex items-center justify-center">+</button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Coaches/Teachers */}
              <div className="sm:col-span-2">
                <label className="block text-[13px] font-medium text-[#374151] mb-2">Coaches/Teachers attending</label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[12px] text-[#6B7280] mb-1">Male</label>
                    <div className="flex items-center">
                      <button type="button" onClick={() => adjustCount('customerStaffMale', -1)}
                        className="w-9 h-9 rounded-l-[8px] border border-r-0 border-[#E5E7EB] bg-surface text-[#6B7280] hover:bg-[#F3F4F6] text-lg font-medium transition flex items-center justify-center">−</button>
                      <input type="number" name="customerStaffMale" value={form.customerStaffMale} onChange={handleChange}
                        min="0" placeholder="0"
                        className="w-full h-9 border border-[#E5E7EB] text-center text-[14px] focus:outline-none focus:ring-2 focus:ring-pb" />
                      <button type="button" onClick={() => adjustCount('customerStaffMale', 1)}
                        className="w-9 h-9 rounded-r-[8px] border border-l-0 border-[#E5E7EB] bg-surface text-[#6B7280] hover:bg-[#F3F4F6] text-lg font-medium transition flex items-center justify-center">+</button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[12px] text-[#6B7280] mb-1">Female</label>
                    <div className="flex items-center">
                      <button type="button" onClick={() => adjustCount('customerStaffFemale', -1)}
                        className="w-9 h-9 rounded-l-[8px] border border-r-0 border-[#E5E7EB] bg-surface text-[#6B7280] hover:bg-[#F3F4F6] text-lg font-medium transition flex items-center justify-center">−</button>
                      <input type="number" name="customerStaffFemale" value={form.customerStaffFemale} onChange={handleChange}
                        min="0" placeholder="0"
                        className="w-full h-9 border border-[#E5E7EB] text-center text-[14px] focus:outline-none focus:ring-2 focus:ring-pb" />
                      <button type="button" onClick={() => adjustCount('customerStaffFemale', 1)}
                        className="w-9 h-9 rounded-r-[8px] border border-l-0 border-[#E5E7EB] bg-surface text-[#6B7280] hover:bg-[#F3F4F6] text-lg font-medium transition flex items-center justify-center">+</button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-[13px] font-medium text-[#374151] mb-1">Additional Requirements</label>
                <textarea
                  name="notes" value={form.notes} onChange={handleChange}
                  rows={4}
                  placeholder="Any specific requests, accommodation preferences, match arrangements, budget range, etc."
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb resize-none"
                />
              </div>
            </div>
          </section>
        )}

        {/* ─── Step 4: Review ───────────────────────────────────────────────── */}
        {step === 4 && (
          <div className="space-y-4">
            {/* Contact */}
            <section className="bg-white rounded-[12px] border border-[#E5E7EB] p-6 space-y-3">
              <h2 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Contact</h2>
              <ReviewRow label="Contact Name" value={form.contactName} />
              <ReviewRow label="Phone" value={form.phone || null} />
            </section>

            {/* Tour details */}
            <section className="bg-white rounded-[12px] border border-[#E5E7EB] p-6 space-y-3">
              <h2 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Tour Details</h2>
              <ReviewRow label="Sport" value={form.sport} />
              <ReviewRow label="Destination" value={form.destination} />
              <ReviewRow label="Departure" value={formatDate(form.departureDate)} />
              <ReviewRow label="Return" value={formatDate(form.returnDate)} />
              {Object.keys(selectedAddons).length > 0 && (
                <div className="flex gap-3">
                  <span className="text-[13px] text-[#6B7280] w-40 flex-shrink-0">What's included</span>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.values(selectedAddons).map((v) => (
                      <span key={v.name} className="px-[9px] py-[3px] rounded-[12px] bg-blue-light border border-pb/20 text-[11px] font-semibold text-pb">{v.name}</span>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* Group info */}
            <section className="bg-white rounded-[12px] border border-[#E5E7EB] p-6 space-y-3">
              <h2 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Group Info</h2>
              <ReviewRow label="Group Type" value={form.groupType} />
              <ReviewRow label="Players/Students" value={(Number(form.participantsMale)||0)+(Number(form.participantsFemale)||0) || null} />
              <ReviewRow label="Male players" value={form.participantsMale || null} />
              <ReviewRow label="Female players" value={form.participantsFemale || null} />
              <ReviewRow label="Male supporters" value={form.supportersMale || null} />
              <ReviewRow label="Female supporters" value={form.supportersFemale || null} />
              <ReviewRow label="Male coaches/teachers" value={form.customerStaffMale || null} />
              <ReviewRow label="Female coaches/teachers" value={form.customerStaffFemale || null} />
              {form.notes && (
                <div className="flex gap-3">
                  <span className="text-[13px] text-[#6B7280] w-40 flex-shrink-0">Additional requirements</span>
                  <span className="text-[13px] text-[#111827] font-medium whitespace-pre-wrap">{form.notes}</span>
                </div>
              )}
            </section>

            {fieldErrors.submit && (
              <p className="text-[13px] text-dr bg-red-light border border-dr/20 rounded-[8px] px-4 py-3">{fieldErrors.submit}</p>
            )}
          </div>
        )}

        {/* ─── Navigation ───────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between pb-4">
          {step > 1 ? (
            <button
              type="button" onClick={goBack}
              className="px-[18px] py-[9px] text-[13px] font-medium text-[#374151] border border-[#E5E7EB] rounded-[8px] hover:bg-surface transition"
            >
              ← Back
            </button>
          ) : (
            <div />
          )}

          <span className="text-[13px] text-[#9CA3AF]">Step {step} of 4</span>

          {step < 4 ? (
            <button
              type="button" onClick={goNext}
              className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition"
            >
              Continue →
            </button>
          ) : (
            <button
              type="button" onClick={handleSubmit} disabled={submitting}
              className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60"
            >
              {submitting ? 'Submitting…' : 'Submit Request'}
            </button>
          )}
        </div>

      </div>
    </div>
  );
}
